import React from 'react';

import { Button } from 'components/base';
import ContractTermsAndConditions from 'components/ContractTermsAndConditions';
import { isFeatureEnabled, FeatureFlag } from 'lib/featureFlags';
import {
  LIGHT_PHONE_III_HANDLE,
  CONTRACT_LIMITED_PLAN_VARIANT_ID,
  CONTRACT_UNLIMITED_PLAN_VARIANT_ID
} from 'constants/Shopify';
import {
  ContractPlanType,
  PhoneModelConfig,
  planPriceLabelShort
} from './types';

const DESCRIPTION =
  'This is a pre-order of the upcoming Light Phone III, an unlocked 5G/4GLTE cell phone.  You can cancel your pre-order at any point prior to shipping for a full refund.  We’ll confirm your address prior to shipping.';

const lightPhoneIIISchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Light Phone III',
  description: DESCRIPTION,
  image: 'https://lightphone.com/assets/images/light-phone.png'
};

const lightPhoneIIIConfig: PhoneModelConfig = {
  key: 'light-phone-iii',
  displayTitle: 'Light Phone III',
  route: '/shop/products/light-phone-iii',
  productHandle: LIGHT_PHONE_III_HANDLE,
  seo: {
    title: 'Light Phone III',
    description: DESCRIPTION,
    schema: lightPhoneIIISchema
  },
  colors: [
    {
      key: 'dark',
      label: 'Dark',
      swatchHex: '#141414',
      productKey: 'lightPhoneIII',
      backgroundClassName: 'bg-color-dark-gray color-gray',
      buttonColor: 'white',
      radioColor: 'gray',
      arrowColor: 'gray'
    },
    {
      key: 'light',
      label: 'Light',
      swatchHex: '#d9d9d9',
      productKey: 'lightPhoneLight',
      backgroundClassName: 'bg-color-gray color-black',
      buttonColor: 'black',
      radioColor: 'light',
      arrowColor: 'black'
    }
  ],
  defaultColorKey: 'dark',
  faqKey: 'lightIIIFAQ',
  phoneStepButtonLabel: 'reserve',
  deliveryEstimate: 'November 2026',
  contractPlans: [
    {
      type: ContractPlanType.LIMITED,
      variantId: CONTRACT_LIMITED_PLAN_VARIANT_ID,
      monthlyPrice: 59,
      descriptionLabel: '1GB data + unlimited calls/texts'
    },
    {
      type: ContractPlanType.UNLIMITED,
      variantId: CONTRACT_UNLIMITED_PLAN_VARIANT_ID,
      monthlyPrice: 89,
      descriptionLabel: 'Unlimited data/calls/texts'
    }
  ],
  purchaseOptionCopy: {
    contractLabel: planPriceLabelShort,
    contractSubheading: (
      <>
        with 2-year Light Service plan
        <br />
        (offer is limited to US customers)
      </>
    ),
    contractDescription: (deliveryDetails) => (
      <>
        <p>Reserve now for delivery:</p>
        <p className="mb1">
          <span style={{ color: 'white', fontSize: '125%' }}>
            {deliveryDetails}
          </span>
        </p>
        <p className="mb1">
          Special pricing for the Light Phone III when you sign up for a two-year
          service plan contract.
          <br />
          First month deposit due today to reserve next available Light Phone
          III.
        </p>
      </>
    ),
    fullPriceHeading: (priceFormatted) => `${priceFormatted} Unlocked`,
    fullPriceDescription: (
      <>
        <p>Reserve now for delivery:</p>
        <p>
          <span style={{ color: 'white', fontSize: '125%' }}>November 2026</span>
        </p>
        <p className="mb2">
          <Button
            ariaLabel="more information about preorders"
            label="Learn more about pre-ordering."
            color="transparent-dust-gray-border"
            variant="underline"
            className="info-text-sm"
            to="/pre-order-details"
            font="akkurat"
          />
        </p>
        <p>
          This is a pre-order for the next available Light Phone III, sold as a
          completely unlocked phone to use on the carrier of your choice.
        </p>
        <p className="mt3">
          NOTE: Outside the US, sales tax/VAT/customs or duty charges may be
          required upon delivery.
        </p>
        <p>
          <Button
            ariaLabel="open compatibility checker"
            label="Learn more about carrier compatibility."
            color="transparent-dust-gray-border"
            variant="underline"
            className="info-text-sm"
            to="/compatibility-checker"
            font="akkurat"
          />
        </p>
      </>
    )
  },
  isPlanPurchaseOptionEnabled: () =>
    isFeatureEnabled(FeatureFlag.PLAN_PURCHASE_OPTION),
  ContractTerms: ContractTermsAndConditions
};

export default lightPhoneIIIConfig;
